import IconModal from '@/assets/smartcity/icon-modal-map.svg';
import MaintenanceIcon from '@/assets/smartcity/maintenance-icon.svg';
import OfflineIcon from '@/assets/smartcity/offline-icon.svg';
import { useCctvStream } from '@/domains/traffic-monitoring/hooks/useCctvStream';
import { useColorStore } from '@/store/colorStore';
import type { VideoMapMapsProps } from './VideoMaps.types';

const VideoMaps = ({ name, streamUrl, typeVideo, onClick }: VideoMapMapsProps) => {
  const { NEWCOLOR } = useColorStore();
  const { videoRef, status } = useCctvStream({ streamUrl });

  const statusConfig = {
    online: { text: 'Online', color: NEWCOLOR.greenPrimary, bg: '#1D293D80', icon: null },
    offline: { text: 'Offline', color: NEWCOLOR.danger, bg: '#FC66051A', icon: OfflineIcon },
    maintenance: {
      text: 'Maintenance',
      color: NEWCOLOR.warning,
      bg: '#FC66051A',
      icon: MaintenanceIcon,
    },
  };

  const statusInfo = statusConfig[typeVideo];
  // A camera the DB reports as online can still have a dead stream - fall
  // back to the offline-style panel instead of a blank <video> in that case.
  const showVideo = typeVideo === 'online' && !!streamUrl && status !== 'error';
  const isBuffering = showVideo && (status === 'loading' || status === 'reconnecting');

  return (
    <div className="z-[99] flex w-[300px] flex-col gap-4 rounded-[16px] bg-white p-5 shadow-lg">
      <div className="flex w-full items-center justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-black" title={name}>
          {name}
        </span>

        <button
          type="button"
          onClick={onClick}
          className="shrink-0 cursor-pointer"
          aria-label="zoom"
        >
          <img src={IconModal} alt="zoom" />
        </button>
      </div>

      <div className="relative aspect-video w-full overflow-hidden rounded-[10px] bg-black">
        <div
          className="absolute left-2 top-2 z-10 inline-flex items-center gap-2 rounded-full border px-3 py-1 backdrop-blur-sm"
          style={{ backgroundColor: statusInfo.bg, borderColor: statusInfo.color }}
        >
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: statusInfo.color }} />
          <span className="text-xs font-semibold uppercase" style={{ color: statusInfo.color }}>
            {statusInfo.text}
          </span>
        </div>

        {showVideo ? (
          <>
            {/* biome-ignore lint/a11y/useMediaCaption: live camera feed, no captions source */}
            <video ref={videoRef} muted playsInline className="h-full w-full object-cover" />
            {isBuffering && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <span className="text-xs text-white">
                  {status === 'reconnecting' ? 'Reconnecting…' : 'Connecting…'}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-db-border">
            {statusInfo.icon ? (
              <img src={statusInfo.icon} alt={statusInfo.text} className="h-16 w-16" />
            ) : (
              <span className="text-sm text-db-grey">No Video</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoMaps;
